import React, { useEffect, useState } from 'react'
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import { RECORD_PER_PAGE_LIMIT } from '../../constants';
import { MaintenanceHistoryDetailsList } from '../../types/maintenance';
import { Card, CircularProgress, Collapse, Divider, List, ListItemButton, ListSubheader, TablePagination, Typography } from '@mui/material';
import * as PXBColors from '@brightlayer-ui/colors';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { EmptyState, Spacer } from '@brightlayer-ui/react-components';
import MaintenanceHistoryListItem from './MaintenanceHistoryListItem';
import { Thunks } from '../../actions';
import { TICKET_STATUS } from '../enum/ENUM';


type Props = {
    title: string
    displayClick: Function;
    organizationType?: string;
    customerType?: string;
    systemId?: string;
    serviceType?: string;
    filterBuilding?: string;
    technicianType?: string;
}


const MaintenanceLogCardFor30Days = (props: Props) => {

    const { t } = useLanguageLocale();
    const [open, setOpen] = useState(true)
    const user = useSelector(Selectors.Usermanagement.usermanagement);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(RECORD_PER_PAGE_LIMIT);
    const dispatch = useDispatch();
    const dateToday = new Date();
    const dateBeforeThirtyDays = new Date(dateToday.getTime() - 30 * 24 * 60 * 60 * 1000);
    const [maintenanceHistoryList, setMaintenanceHistoryList] = useState<MaintenanceHistoryDetailsList>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setCurrentPage(newPage);
        fetchData(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
        fetchData(0, parseInt(event.target.value, 10));
    };

    const handleClick = () => {
        setOpen(!open);
    };

    const fetchData = (offset: number = currentPage, limit: number = rowsPerPage) => {
        dispatch(Thunks.UserManagement.getMaintenanceDetail30Days(
            props.organizationType,
            "",
            props.technicianType,
            "",
            props.customerType,
            "",
            props.systemId,
            props.filterBuilding,
            (offset + 1).toString(),
            limit.toString(),
            "",
            "",
            dateBeforeThirtyDays.toISOString(),
            TICKET_STATUS.STATUS_COMPLETED,
            props.serviceType ? Number(props.serviceType) : undefined
        ))
    }

    useEffect(() => {
        setCurrentPage(0);
        setRowsPerPage(RECORD_PER_PAGE_LIMIT);
        fetchData(0, RECORD_PER_PAGE_LIMIT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.organizationType,
        props.customerType,
        props.systemId,
        props.serviceType,
        props.filterBuilding,
        props.technicianType
    ]);

    useEffect(() => {
        if (!user.maintenanceDetail30DaysLoading && user.maintenanceDetail30DaysSuccess) {
            if (user.maintenanceDetail30Days.detail.length > 0) {
                setMaintenanceHistoryList(user.maintenanceDetail30Days.detail);
                setTotalRecords(user.maintenanceDetail30Days.totalRecordCount);
            } else {
                setMaintenanceHistoryList([]);
                setTotalRecords(0);

            }
        } else if (!user.maintenanceDetail30DaysLoading && user.maintenanceDetail30DaysFail) {
            setMaintenanceHistoryList([]);
            setTotalRecords(0);
        }
    }, [user.maintenanceDetail30DaysLoading, user.maintenanceDetail30DaysSuccess, user.maintenanceDetail30DaysFail]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <Card style={{ flex: '1 1 0px', overflow: 'auto' }} data-testid="maintenance-logs-details-container">
                <List
                    component="div"
                    disablePadding
                    id="maintenance-logs-list"
                    data-testid="maintenance-logs-list"
                    style={{ minHeight: 'auto' }}
                    subheader={
                        <>
                            <ListSubheader color="primary"
                                component="div"
                                data-testid="maintenance-logs-list-header"
                                id="maintenance-logs-list-header"
                                style={{
                                    display: 'flex',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    alignItems: 'center',
                                    overflow: 'auto',
                                    whiteSpace: 'nowrap',
                                    marginLeft: 10,
                                    height: 45,
                                }}>

                                <Typography
                                    variant="body2"
                                    style={{
                                        alignItems: 'left',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: PXBColors.blue[400],
                                        width: '92.3%',
                                        paddingLeft: '10',
                                    }}
                                    id="maintenance-logs-title"
                                    data-testid="maintenance-logs-title"
                                >

                                    {props.title}
                                </Typography>

                                <ListItemButton
                                    onClick={handleClick}
                                    id="list-header-item-button"
                                    style={{
                                        width: '7.41%',
                                    }}
                                >
                                    {open ? (
                                        <KeyboardArrowUp data-testid="arrow-up" id="list-header-up-icon" />
                                    ) : (
                                        <KeyboardArrowDown data-testid="arrow-down" id="list-header-down-icon" />
                                    )}
                                </ListItemButton>

                            </ListSubheader>
                            <Divider />
                        </>
                    }>
                    {user.maintenanceDetail30DaysLoading && <EmptyState style={{ flex: 1, minHeight: 300, backgroundColor: PXBColors.white[50] }}
                        icon={<CircularProgress id="progress-spinner" />} title="" placeholder={undefined} />}
                    <Collapse in={open} timeout="auto" unmountOnExit>

                        {(maintenanceHistoryList.length === 0 && !user.maintenanceDetail30DaysLoading) && <EmptyState style={{ flex: 1, height: 200 }}
                            title={t('MAINTENANCE_DETAILS.NO_RECORD_FOUND')} icon={undefined} placeholder={undefined} />}

                        <div data-testid="maintenance-History-List-Item">
                            {!user.maintenanceDetail30DaysLoading && maintenanceHistoryList?.map((value) => (
                                <MaintenanceHistoryListItem
                                    key={value.id}
                                    data={{
                                        id: value.id,
                                        customerName: value.customerName,
                                        technicianName: value.technicianName,
                                        completionDate: value.completionDate,
                                        serviceType: value.serviceType,
                                        ticketNumber: value.ticketNumber
                                    }}
                                    displayClick={props.displayClick}
                                />
                            ))}
                        </div>

                        {maintenanceHistoryList.length > 0 && <TablePagination
                            component="div"
                            data-testid="pagination-maintenance-history"
                            count={totalRecords}
                            page={currentPage}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage={t("LABELS.ITEMS_PER_PAGE")}
                            showFirstButton showLastButton
                        />}
                    </Collapse>
                </List>
            </Card>
            <Spacer style={{ height: 16 }} placeholder={undefined} />
        </>
    )
}

export default MaintenanceLogCardFor30Days
